import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney } from '@/utils/format';
import { balanceAfterAdvance, isApprovable, summarizeChecks } from '@/utils/invoice';
import type { Invoice } from '@/types/portal';

interface InvoiceDecisionPanelProps {
  invoice: Invoice;
  status: string;
  canApprove: boolean;
  note: string;
  onNoteChange: (value: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onHold: () => void;
}

export default function InvoiceDecisionPanel({
  invoice,
  status,
  canApprove,
  note,
  onNoteChange,
  onApprove,
  onReject,
  onHold,
}: InvoiceDecisionPanelProps) {
  const summary = summarizeChecks(invoice);
  const approvable = isApprovable(invoice);
  const balance = balanceAfterAdvance(invoice.amount, invoice.advance);
  const locked = status === 'Paid' || status === 'Rejected';

  const recommendation = approvable
    ? 'All blocking checks passed. This invoice matches the contracted rate, the deliverable is approved, and the payout wallet is ready.'
    : `Hold this invoice — ${summary.fails} blocking check${summary.fails === 1 ? '' : 's'} must be resolved before payout. ${invoice.flags[0] ?? ''}`;

  return (
    <div className="lg:sticky lg:top-20">
      <section className="rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Review decision
          </h2>
          <StatusBadge status={status} />
        </header>

        <div className="px-5 py-4">
          <div className="rounded-md border border-accent-200 bg-accent-50 p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-label font-semibold uppercase tracking-wider text-accent-800">
              <i className="ri-sparkling-2-line" />
              AI recommendation
            </p>
            <p className="mt-1 text-sm leading-relaxed text-accent-900">{recommendation}</p>
          </div>

          <dl className="mt-4 space-y-2.5 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Invoice total</dt>
              <dd className="text-foreground-800">
                {formatMoney(invoice.amount, invoice.currency)}
              </dd>
            </div>
            {invoice.advance ? (
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">
                  Early payout advance ({invoice.advance.percent}%)
                </dt>
                <dd className="text-secondary-800">
                  − {formatMoney(invoice.advance.amount, invoice.currency)}
                </dd>
              </div>
            ) : null}
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Balance payable</dt>
              <dd className="font-heading text-base font-semibold text-foreground-950">
                {formatMoney(balance, invoice.currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Blocking checks</dt>
              <dd className={summary.fails > 0 ? 'font-medium text-primary-700' : 'text-foreground-800'}>
                {summary.fails}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Warnings</dt>
              <dd className="text-foreground-800">{summary.warns}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Payout rail</dt>
              <dd className="text-foreground-800">{invoice.rail}</dd>
            </div>
          </dl>

          <div className="mt-4">
            <label
              htmlFor="invoice-review-note"
              className="text-xs font-label font-medium text-foreground-600"
            >
              Decision note
            </label>
            <textarea
              id="invoice-review-note"
              value={note}
              maxLength={500}
              onChange={(e) => onNoteChange(e.target.value)}
              rows={3}
              disabled={!canApprove || locked}
              placeholder="Add context for the audit trail..."
              className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-background-100"
            />
            <p className="mt-1 text-right text-[11px] text-foreground-400">{note.length}/500</p>
          </div>

          {locked ? (
            <div className="mt-3 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
              {status === 'Paid'
                ? 'This invoice has been paid and is locked for editing.'
                : 'This invoice was rejected. Ask the creator to resubmit a corrected version.'}
            </div>
          ) : canApprove ? (
            <div className="mt-3 space-y-2">
              <Button
                icon="ri-check-line"
                className="w-full"
                onClick={onApprove}
                disabled={status === 'Approved'}
              >
                {status === 'Approved' ? 'Already approved' : 'Approve invoice'}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  icon="ri-close-line"
                  className="flex-1"
                  onClick={onReject}
                >
                  Reject
                </Button>
                <Button
                  variant="outline"
                  icon="ri-pause-circle-line"
                  className="flex-1"
                  onClick={onHold}
                  disabled={status === 'On hold'}
                >
                  {status === 'On hold' ? 'On hold' : 'Put on hold'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
              <i className="ri-lock-line mt-0.5" />
              Your role can view this invoice but not approve it.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}